// Minimal API client for the AcademiX backend
// If REACT_APP_USE_PROXY=1 prefer relative paths so CRA proxy (package.json) forwards to mock.
import supabase from './supabaseClient';

const useProxy = ['1', 'true', 'yes', 'on'].includes(String(process.env.REACT_APP_USE_PROXY || '').toLowerCase());
const API_BASE = useProxy ? '' : (process.env.REACT_APP_API_URL || (process.env.REACT_APP_USE_MOCK ? 'http://localhost:4000' : 'http://localhost:3000'));
let runtimeDevToken = '';
const DISABLE_AUTH_EXPIRE_EVENTS = true;

function parseJwtPayload(token) {
  try {
    const raw = String(token || '').replace(/^Bearer\s+/i, '').trim();
    if (!raw) return null;
    const parts = raw.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

function isJwtExpired(token) {
  const payload = parseJwtPayload(token);
  const exp = Number(payload?.exp || 0);
  if (!exp) return false;
  return Date.now() >= exp * 1000;
}

function notifyExpiredAuth() {
  if (DISABLE_AUTH_EXPIRE_EVENTS) return;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('AcademiX.authExpired'));
}

function withQuery(path, params = {}) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).length) q.set(k, String(v));
  });
  const qs = q.toString();
  return `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
}

export function setRuntimeDevToken(token) {
  runtimeDevToken = String(token || '').trim();
}

export async function studentLogin(loginId, password) {
  const url = `${API_BASE}/auth/student/login`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, password })
  });
  return res.json();
}

export async function teacherLogin(loginId, password) {
  const url = `${API_BASE}/auth/teacher/login`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, password })
  });
  return res.json();
}

export async function schoolRegister(payload) {
  const url = `${API_BASE}/auth/school/register`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolRegisterRequestOtp(payload) {
  const url = `${API_BASE}/auth/school/register/request-otp`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolRegisterVerifyOtp(email, otp) {
  const url = `${API_BASE}/auth/school/register/verify-otp`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp })
  });
  return res.json();
}

export async function schoolLogin(email, password) {
  const url = `${API_BASE}/auth/school/login`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  return res.json();
}

export async function getInviteInfo(token) {
  return getJson(`${API_BASE}/auth/invite/${encodeURIComponent(token)}`);
}

export async function acceptInvite(payload) {
  const url = `${API_BASE}/auth/invite/accept`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

async function authHeaders() {
  const runtimeToken = String(runtimeDevToken || '').trim();
  if (runtimeToken && !isJwtExpired(runtimeToken)) {
    return { Authorization: `Bearer ${runtimeToken}`, 'Content-Type': 'application/json' };
  }
  if (runtimeToken && isJwtExpired(runtimeToken)) notifyExpiredAuth();

  // Session fallback: after page reload there can be a brief window before
  // runtime token is rehydrated into memory.
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('AcademiX.session');
      const parsed = raw ? JSON.parse(raw) : null;
      const sessionToken = String(parsed?.token || '').trim();
      if (sessionToken && !isJwtExpired(sessionToken)) {
        return { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' };
      }
      if (sessionToken && isJwtExpired(sessionToken)) notifyExpiredAuth();
    }
  } catch {
    // Ignore localStorage parse/access issues.
  }

  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token && !isJwtExpired(token)) return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (token && isJwtExpired(token)) notifyExpiredAuth();
  } catch (e) {
    // ignore
  }
  const devToken = String(process.env.REACT_APP_DEV_TOKEN || '').trim();
  if (devToken && !isJwtExpired(devToken)) return { Authorization: `Bearer ${devToken}`, 'Content-Type': 'application/json' };
  if (devToken && isJwtExpired(devToken)) notifyExpiredAuth();
  return { 'Content-Type': 'application/json' };
}

async function getJson(url) {
  const headers = await authHeaders();
  const res = await fetch(url, { headers });
  return res.json();
}

async function getJsonStrict(url, label) {
  const headers = await authHeaders();
  const res = await fetch(url, { headers });
  if (res.status === 401 || res.status === 403) notifyExpiredAuth();
  if (!res.ok) throw new Error(`${label} failed: ${res.status}`);
  return res.json();
}

// Throws if the backend returned { success: false, error: "..." } at HTTP 200
function checkSuccess(data, label) {
  if (data && data.success === false) {
    throw new Error(data.error || `${label} returned an error`);
  }
  return data;
}

async function getJsonChecked(url, label) {
  const data = await getJsonStrict(url, label);
  return checkSuccess(data, label);
}

export async function sendChat(studentId, message, personality, conversationId, recentMessages, imageDataUrl, imageDataUrls, imageNames, lessonId, lessonTitle, lessonSubject, signal) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({ studentId, message, personality, conversationId, recentMessages, imageDataUrl, imageDataUrls, imageNames, lessonId, lessonTitle, lessonSubject })
  });
  return res.json();
}

export async function getChatHistory(studentId, conversationId) {
  let url = `${API_BASE}/chat/history?studentId=${encodeURIComponent(studentId)}`;
  if (conversationId) url += `&conversationId=${encodeURIComponent(conversationId)}`;
  return getJsonChecked(url, 'getChatHistory');
}

export async function getLearningTimeline(studentId, limit = 20) {
  const safeLimit = Number.isFinite(Number(limit)) ? Number(limit) : 20;
  const url = `${API_BASE}/chat/learning-timeline?studentId=${encodeURIComponent(studentId)}&limit=${encodeURIComponent(String(safeLimit))}`;
  return getJsonChecked(url, 'getLearningTimeline');
}

export async function generateLocalTtsAudio(text, targetLanguage = 'en-US', studentId, voice, speed = 1) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/tts-audio`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, targetLanguage, studentId, voice, speed })
  });
  const data = await res.json();
  return checkSuccess(data, 'generateLocalTtsAudio');
}

export async function transcribeTutorAudio(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/transcribe`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...(payload || {}) })
  });
  const data = await res.json();
  return checkSuccess(data, 'transcribeTutorAudio');
}

export async function listMemories(studentId) {
  // Backend exposes GET /chat/memories which returns { memories: [...] }
  const url = `${API_BASE}/chat/memories?studentId=${encodeURIComponent(studentId)}`;
  const headers = await authHeaders();
  const res = await fetch(url, { headers });
  const data = await res.json();
  // Normalize common shapes: { data: [...] } | { memories: [...] } | raw array
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.memories)) return data.memories;
  return [];
}

export async function addMemory(studentId, value) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/memory`, {
    method: 'POST', headers, body: JSON.stringify({ studentId, value })
  });
  return res.json();
}

export async function getStudent(studentId) {
  const url = `${API_BASE}/chat/student?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getStudent');
}

export async function saveStudent(payload) {
  const url = `${API_BASE}/chat/student`;
  const headers = await authHeaders();
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  return res.json();
}

export async function getDashboard(studentId) {
  const url = `${API_BASE}/dashboard?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getDashboard');
}

// ─── Knowledge Orchard ──────────────────────────────────────────────────────
export async function getOrchard(studentId) {
  const url = `${API_BASE}/orchard?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getOrchard');
}

export async function getOrchardTree(studentId, subjectKey) {
  const url = `${API_BASE}/orchard/${encodeURIComponent(subjectKey)}?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getOrchardTree');
}

export async function recordOrchardActivity(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/orchard/activity`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  return checkSuccess(data, 'recordOrchardActivity');
}

export async function completeOrchardReview(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/orchard/review/complete`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  return checkSuccess(data, 'completeOrchardReview');
}

// ─── Learning Games ──────────────────────────────────────────────────────────
export async function getGamesCatalog() {
  return getJsonChecked(`${API_BASE}/games`, 'getGamesCatalog');
}

export async function getFlashcardOverview(studentId) {
  const url = `${API_BASE}/games/flashcards/overview?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getFlashcardOverview');
}

export async function getFlashcardCards(studentId, { subject, deckId, scope = 'all', mode = 'review', limit = 20 } = {}) {
  const params = new URLSearchParams({ studentId, subject: subject || '', scope, mode, limit: String(limit) });
  if (deckId) params.set('deckId', deckId);
  return getJsonChecked(`${API_BASE}/games/flashcards/cards?${params.toString()}`, 'getFlashcardCards');
}

export async function submitFlashcardReview(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/games/flashcards/review`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  return checkSuccess(data, 'submitFlashcardReview');
}

export async function logGameSession(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/games/session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  return checkSuccess(data, 'logGameSession');
}

export async function completeFlashcardChapter(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/games/flashcards/complete-chapter`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  return checkSuccess(data, 'completeFlashcardChapter');
}

export async function getQuizRushOverview(studentId) {
  const url = `${API_BASE}/games/quiz-rush/overview?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getQuizRushOverview');
}

export async function getQuizRushQuestions(studentId, { subject, deckId, scope = 'all', limit = 10 } = {}) {
  const params = new URLSearchParams({ studentId, subject: subject || '', scope, limit: String(limit) });
  if (deckId) params.set('deckId', deckId);
  return getJsonChecked(`${API_BASE}/games/quiz-rush/questions?${params.toString()}`, 'getQuizRushQuestions');
}

export async function getHomework(studentId) {
  const url = `${API_BASE}/homework?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getHomework');
}

export async function uploadHomeworkImage(file) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/homework/upload`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fileName: file?.name || 'upload.png',
      mimeType: file?.type || 'image/png',
      data: await fileToDataUrl(file)
    })
  });
  if (!res.ok) throw new Error(`uploadHomeworkImage failed: ${res.status}`);
  const json = await res.json();
  // Convert relative URL to absolute so images work across different frontend ports
  if (json?.url && String(json.url).startsWith('/')) {
    json.url = `${API_BASE}${json.url}`;
  }
  return json;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file selected'));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });
}

export async function submitHomework(homeworkId, studentId, answers, attachmentUrls) {
  const list = Array.isArray(attachmentUrls)
    ? attachmentUrls.filter((u) => typeof u === 'string' && u.trim())
    : (typeof attachmentUrls === 'string' && attachmentUrls.trim() ? [attachmentUrls.trim()] : []);
  const headers = await authHeaders();
  const url = `${API_BASE}/homework/${encodeURIComponent(homeworkId)}/submit`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      studentId,
      answers,
      attachmentUrls: list,
      // Keep legacy field for older backend handlers.
      attachmentUrl: list[0] || null
    })
  });
  if (!res.ok) throw new Error(`submitHomework failed: ${res.status}`);
  const json = await res.json();
  if (json && json.success === false) {
    const msg = String(json.error || 'submitHomework failed');
    // Local dev resilience: backend mock has intermittently emitted null-map errors
    // while still processing side effects; do not hard-block student submit UX.
    if (msg.toLowerCase().includes("reading 'map'") || msg.toLowerCase().includes('reading "map"')) {
      return { success: true, attemptId: json.attemptId || null, grade: json.grade ?? null, warning: msg };
    }
    throw new Error(msg);
  }
  return json;
}

export async function getHomeworkAttempts(homeworkId, studentId) {
  const url = `${API_BASE}/homework/${encodeURIComponent(homeworkId)}/attempts?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getHomeworkAttempts');
}

export async function getProgress(studentId, period = 'week') {
  const url = `${API_BASE}/progress?studentId=${encodeURIComponent(studentId)}&period=${encodeURIComponent(period)}`;
  return getJsonChecked(url, 'getProgress');
}

export async function getLearningScore(studentId) {
  const url = `${API_BASE}/progress/learning-score?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getLearningScore');
}

export async function getCalendar(studentId) {
  const url = `${API_BASE}/calendar?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getCalendar');
}

export async function createCalendarEvent(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/calendar`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload || {}) });
  if (!res.ok) throw new Error(`createCalendarEvent failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'Calendar create failed');
  return data;
}

export async function updateCalendarEvent(eventId, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/calendar/${encodeURIComponent(eventId)}`;
  const res = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(payload || {}) });
  if (!res.ok) throw new Error(`updateCalendarEvent failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'Calendar update failed');
  return data;
}

export async function deleteCalendarEvent(eventId, studentId) {
  const headers = await authHeaders();
  const url = `${API_BASE}/calendar/${encodeURIComponent(eventId)}?studentId=${encodeURIComponent(studentId)}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(`deleteCalendarEvent failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'Calendar delete failed');
  return data;
}

export async function getRewards(studentId) {
  const url = `${API_BASE}/rewards?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getRewards');
}

export async function earnReward(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/rewards/earn`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload || {}) });
  if (!res.ok) throw new Error(`earnReward failed: ${res.status}`);
  return res.json();
}

export async function checkInReward(studentId) {
  const headers = await authHeaders();
  const url = `${API_BASE}/rewards/checkin`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ studentId }) });
  if (!res.ok) throw new Error(`checkInReward failed: ${res.status}`);
  return res.json();
}

export async function getTests(studentId, filter = 'upcoming') {
  const url = `${API_BASE}/tests?studentId=${encodeURIComponent(studentId)}&filter=${encodeURIComponent(filter)}`;
  return getJsonChecked(url, 'getTests');
}

export async function startTest(testId, studentId) {
  const headers = await authHeaders();
  const url = `${API_BASE}/tests/${encodeURIComponent(testId)}/start`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId })
  });
  return res.json();
}

export async function submitTestAttempt(attemptId, studentId, answers) {
  const headers = await authHeaders();
  const url = `${API_BASE}/tests/attempts/${encodeURIComponent(attemptId)}/submit`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, answers })
  });
  return res.json();
}

export async function getTestAttempt(attemptId) {
  const url = `${API_BASE}/tests/attempts/${encodeURIComponent(attemptId)}`;
  return getJsonChecked(url, 'getTestAttempt');
}

export async function getTestReview(testId, studentId) {
  const url = `${API_BASE}/tests/${encodeURIComponent(testId)}/review?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getTestReview');
}

export async function getLibrary(topic, level, page = 1) {
  const params = new URLSearchParams();
  if (topic) params.set('topic', topic);
  if (level) params.set('level', level);
  params.set('page', String(page));
  const url = `${API_BASE}/library?${params.toString()}`;
  return getJsonChecked(url, 'getLibrary');
}

export async function getLibraryResource(id) {
  const url = `${API_BASE}/library/${encodeURIComponent(id)}`;
  return getJsonChecked(url, 'getLibraryResource');
}

export async function getSettings(studentId) {
  const url = `${API_BASE}/settings?studentId=${encodeURIComponent(studentId)}`;
  return getJsonChecked(url, 'getSettings');
}

export async function saveSettings(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/settings`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function recordProgress(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/progress`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`recordProgress failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'recordProgress failed');
  return data;
}

export async function assignTest(testId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}/assign`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`assignTest failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'assignTest failed');
  return data;
}

export async function createTest(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`createTest failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'createTest failed');
  return data;
}

export async function addTestQuestion(testId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}/questions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`addTestQuestion failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'addTestQuestion failed');
  return data;
}

export async function updateTestQuestion(testId, questionId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}/questions/${encodeURIComponent(questionId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`updateTestQuestion failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'updateTestQuestion failed');
  return data;
}

export async function deleteTestQuestion(testId, questionId) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}/questions/${encodeURIComponent(questionId)}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error(`deleteTestQuestion failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'deleteTestQuestion failed');
  return data;
}

export async function listTestQuestions(testId) {
  const url = `${API_BASE}/tests/${encodeURIComponent(testId)}/questions`;
  return getJsonChecked(url, 'listTestQuestions');
}

export async function deleteTest(testId) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error(`deleteTest failed: ${res.status}`);
  return res.json();
}

export async function updateTest(testId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`updateTest failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'updateTest failed');
  return data;
}

export async function cloneTest(testId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/tests/${encodeURIComponent(testId)}/clone`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`cloneTest failed: ${res.status}`);
  const data = await res.json();
  if (data && data.success === false) throw new Error(data.error || 'cloneTest failed');
  return data;
}

export async function getTeacherDashboard() {
  const res = await fetch(withQuery('/teacher/dashboard'), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherDashboard failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherDashboard');
}

export async function getTeacherProfile() {
  const res = await fetch(`${API_BASE}/teacher/profile`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherProfile failed: ${res.status}`);
  return res.json();
}

export async function getTeacherStudents(queryOrParams) {
  const params = typeof queryOrParams === 'string'
    ? { q: String(queryOrParams || '').trim() }
    : {
        q: String(queryOrParams?.q || '').trim(),
        className: String(queryOrParams?.className || '').trim()
      };
  const normalized = {};
  if (params.q) normalized.q = params.q;
  if (params.className && params.className.toLowerCase() !== 'all') normalized.className = params.className;

  const res = await fetch(withQuery('/teacher/students', normalized), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudents failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudents');
}

export async function bulkUpdateTeacherStudentsClass(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/teacher/students/bulk/class`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`bulkUpdateTeacherStudentsClass failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'bulkUpdateTeacherStudentsClass');
}

export async function getTeacherStudentProgress(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/progress`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentProgress failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudentProgress');
}

export async function getTeacherStudentSubjectProgress(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/subject-progress`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentSubjectProgress failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudentSubjectProgress');
}

export async function getTeacherStudentLearningScore(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/learning-score`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentLearningScore failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudentLearningScore');
}

export async function getTeacherStudentDeliveryStatus(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/delivery-status`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentDeliveryStatus failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudentDeliveryStatus');
}

// ── Report cards ───────────────────────────────────────────────────────────

async function sendReportCardJson(method, path, payload, label) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: await authHeaders(),
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`${label} failed: ${res.status}`);
  return checkSuccess(await res.json(), label);
}

export async function getReportCardExams(className) {
  return getJsonChecked(withQuery('/teacher/report-cards/exams', { className }), 'getReportCardExams');
}

export async function createReportCardExam(payload) {
  return sendReportCardJson('POST', '/teacher/report-cards/exams', payload, 'createReportCardExam');
}

export async function updateReportCardExam(examId, payload) {
  return sendReportCardJson('PATCH', `/teacher/report-cards/exams/${encodeURIComponent(examId)}`, payload, 'updateReportCardExam');
}

export async function getReportCardMarks(examId) {
  return getJsonChecked(`${API_BASE}/teacher/report-cards/exams/${encodeURIComponent(examId)}/marks`, 'getReportCardMarks');
}

export async function saveReportCardMarks(examId, entries) {
  return sendReportCardJson('POST', `/teacher/report-cards/exams/${encodeURIComponent(examId)}/marks`, { entries }, 'saveReportCardMarks');
}

export async function getStudentReportCards() {
  return getJsonChecked(`${API_BASE}/progress/report-cards`, 'getStudentReportCards');
}

// Teacher view of one student's released report cards (+ which exams are still pending).
export async function getTeacherStudentReportCards(studentId) {
  return getJsonChecked(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/report-cards`, 'getTeacherStudentReportCards');
}

export async function getTeacherStudentActivity(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/activity`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentActivity failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherStudentActivity');
}

export async function getTeacherStudentHomework(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/homework`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentHomework failed: ${res.status}`);
  return res.json();
}

export async function getTeacherStudentTestAttempts(studentId) {
  const res = await fetch(`${API_BASE}/teacher/students/${encodeURIComponent(studentId)}/test-attempts`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherStudentTestAttempts failed: ${res.status}`);
  return res.json();
}

export async function gradeTeacherHomework(hwId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/teacher/homework/${encodeURIComponent(hwId)}/grade`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function aiGradeTeacherHomework(hwId) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/teacher/homework/${encodeURIComponent(hwId)}/ai-grade`, {
    method: 'POST',
    headers
  });
  return res.json();
}

export async function schoolDashboard() {
  const res = await fetch(withQuery('/school/dashboard'), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`schoolDashboard failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'schoolDashboard');
}

export async function schoolTeachers(params) {
  const res = await fetch(withQuery('/school/teachers', params || {}), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`schoolTeachers failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'schoolTeachers');
}

export async function schoolInvites(params) {
  const query = { ...(params || {}) };
  if (String(query.status || 'all') === 'all') delete query.status;
  const res = await fetch(withQuery('/school/invites', query), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`schoolInvites failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'schoolInvites');
}

export async function schoolStudents(params) {
  const res = await fetch(withQuery('/school/students', params || {}), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`schoolStudents failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'schoolStudents');
}

export async function schoolRegisterTeacher(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/teachers/register`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolDeduplicateTeachers() {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/teachers/deduplicate`;
  const res = await fetch(url, {
    method: 'POST',
    headers
  });
  return res.json();
}

export async function schoolInviteTeacher(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/teacher`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolUpdateTeacher(teacherId, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/teachers/${encodeURIComponent(teacherId)}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolResetTeacherPassword(teacherId, password) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/teachers/${encodeURIComponent(teacherId)}/reset-password`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ password })
  });
  return res.json();
}

export async function schoolDeleteTeacher(teacherId) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/teachers/${encodeURIComponent(teacherId)}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  return res.json();
}

export async function revokeSchoolTeacherInvite(token) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/teacher/${encodeURIComponent(token)}/revoke`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  return res.json();
}

export async function resendSchoolTeacherInvite(token, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/teacher/${encodeURIComponent(token)}/resend`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolRegisterStudent(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/students/register`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolUpdateStudent(studentId, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/students/${encodeURIComponent(studentId)}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function schoolResetStudentPassword(studentId, password) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/students/${encodeURIComponent(studentId)}/reset-password`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ password })
  });
  return res.json();
}

export async function schoolDeleteStudent(studentId) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/students/${encodeURIComponent(studentId)}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  return res.json();
}

export async function schoolInviteStudent(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/student`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function revokeSchoolStudentInvite(token) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/student/${encodeURIComponent(token)}/revoke`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  return res.json();
}

export async function resendSchoolStudentInvite(token, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/school/invites/student/${encodeURIComponent(token)}/resend`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function assignTeacherHomework(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/teacher/homework/assign`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (res.status === 401 || res.status === 403) notifyExpiredAuth();
  if (!res.ok) throw new Error(`assignTeacherHomework failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'assignTeacherHomework');
}

export async function updateTeacherHomework(homeworkId, payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/teacher/homework/${encodeURIComponent(homeworkId)}/update`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (res.status === 401 || res.status === 403) notifyExpiredAuth();
  if (!res.ok) throw new Error(`updateTeacherHomework failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'updateTeacherHomework');
}

export async function resyncTeacherHomework(assignments) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/teacher/homework/resync`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ assignments })
  });
  return res.json();
}

export async function getTeacherAssignedHomework() {
  const res = await fetch(`${API_BASE}/teacher/homework`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherAssignedHomework failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherAssignedHomework');
}

export async function getTeacherHomeworkAttempts(hwId) {
  const res = await fetch(`${API_BASE}/teacher/homework/${encodeURIComponent(hwId)}/attempts`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherHomeworkAttempts failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherHomeworkAttempts');
}

export async function getTeacherAnnouncements() {
  const res = await fetch(withQuery('/teacher/announcements'), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`getTeacherAnnouncements failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'getTeacherAnnouncements');
}

export async function postTeacherAnnouncement(payload) {
  const headers = await authHeaders();
  const url = `${API_BASE}/teacher/announcements`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function updateTeacherAnnouncement(id, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/teacher/announcements/${encodeURIComponent(id)}/update`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) throw new Error(`updateTeacherAnnouncement failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'updateTeacherAnnouncement');
}

export async function deleteTeacherAnnouncement(id) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/teacher/announcements/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error(`deleteTeacherAnnouncement failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'deleteTeacherAnnouncement');
}

export async function askTeacherAi(prompt) {
  const headers = await authHeaders();
  const url = `${API_BASE}/teacher/ai/assist`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ prompt })
  });
  return res.json();
}

export async function createCurriculumLesson(payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/curriculum/lessons`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function listCurriculumLessons(params = {}) {
  const res = await fetch(withQuery('/curriculum/lessons', params || {}), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`listCurriculumLessons failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'listCurriculumLessons');
}

export async function setCurriculumLessonVisibility(lessonId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}/visibility`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function uploadCurriculumLessonDocument(lessonId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}/documents/upload`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function listCurriculumLessonDocuments(lessonId) {
  const res = await fetch(`${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}/documents`, {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`listCurriculumLessonDocuments failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'listCurriculumLessonDocuments');
}

// Subjects that actually have a teacher for a class, with that teacher. The
// admin curriculum panel refuses to file a lesson under anything else.
export async function listCurriculumSubjects(className) {
  const res = await fetch(withQuery('/curriculum/subjects', { className: className || '' }), {
    headers: await authHeaders()
  });
  if (!res.ok) throw new Error(`listCurriculumSubjects failed: ${res.status}`);
  const data = await res.json();
  return checkSuccess(data, 'listCurriculumSubjects');
}

export async function updateCurriculumLesson(lessonId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload || {})
  });
  return res.json();
}

export async function deleteCurriculumLesson(lessonId) {
  const res = await fetch(`${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}`, {
    method: 'DELETE',
    headers: await authHeaders()
  });
  return res.json();
}

export async function deleteCurriculumLessonDocument(lessonId, documentId) {
  const res = await fetch(
    `${API_BASE}/curriculum/lessons/${encodeURIComponent(lessonId)}/documents/${encodeURIComponent(documentId)}`,
    { method: 'DELETE', headers: await authHeaders() }
  );
  return res.json();
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERACTIVE LEARNING APIs
// ═══════════════════════════════════════════════════════════════════════════════

export async function generateCheckQuestion(studentId, conversationId, lessonId, lessonTitle, lessonSubject) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/check-question`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, conversationId, lessonId, lessonTitle, lessonSubject })
  });
  return res.json();
}

export async function answerCheckQuestion(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/answer-check`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...payload })
  });
  return res.json();
}

export async function evaluateExplainBack(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/explain-back`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...payload })
  });
  return res.json();
}

export async function generateLessonStory(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/story/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...payload })
  });
  return res.json();
}

export async function completeLessonStory(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/story/complete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...payload })
  });
  return res.json();
}

export async function generateQuizRush(studentId, opts) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/quiz-rush/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...opts })
  });
  return res.json();
}

export async function submitQuizRush(studentId, payload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/chat/quiz-rush/submit`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ studentId, ...payload })
  });
  return res.json();
}

export async function getDueReviewNudge(studentId, subject) {
  let url = `${API_BASE}/chat/due-review?studentId=${encodeURIComponent(studentId)}`;
  if (subject) url += `&subject=${encodeURIComponent(subject)}`;
  return getJsonChecked(url, 'getDueReviewNudge');
}

const api = {
  sendChat,
  listMemories,
  addMemory,
  getStudent,
  saveStudent,
  getDashboard,
  getHomework,
  submitHomework,
  getHomeworkAttempts,
  getProgress,
  recordProgress,
  getCalendar,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getRewards,
  earnReward,
  checkInReward,
  getChatHistory,
  getLearningTimeline,
  generateLocalTtsAudio,
  getOrchard,
  getOrchardTree,
  recordOrchardActivity,
  completeOrchardReview,
  getTests,
  startTest,
  submitTestAttempt,
  getTestAttempt,
  getTestReview,
  createTest,
  assignTest,
  addTestQuestion,
  updateTestQuestion,
  deleteTestQuestion,
  listTestQuestions,
  deleteTest,
  updateTest,
  cloneTest,
  getLibrary,
  getLibraryResource,
  getSettings,
  saveSettings,
  getTeacherDashboard,
  getTeacherStudents,
  bulkUpdateTeacherStudentsClass,
  getTeacherStudentProgress,
  getTeacherStudentSubjectProgress,
  getTeacherStudentLearningScore,
  getTeacherStudentDeliveryStatus,
  assignTeacherHomework,
  updateTeacherHomework,
  getTeacherAssignedHomework,
  getTeacherHomeworkAttempts,
  getTeacherAnnouncements,
  postTeacherAnnouncement,
  updateTeacherAnnouncement,
  deleteTeacherAnnouncement,
  askTeacherAi,
  createCurriculumLesson,
  listCurriculumLessons,
  setCurriculumLessonVisibility,
  uploadCurriculumLessonDocument,
  listCurriculumLessonDocuments,
  listCurriculumSubjects,
  updateCurriculumLesson,
  deleteCurriculumLesson,
  deleteCurriculumLessonDocument,
  studentLogin,
  teacherLogin,
  schoolRegister,
  schoolRegisterRequestOtp,
  schoolRegisterVerifyOtp,
  schoolLogin,
  getInviteInfo,
  acceptInvite,
  schoolDashboard,
  schoolTeachers,
  schoolInvites,
  schoolStudents,
  schoolRegisterTeacher,
  schoolInviteTeacher,
  schoolUpdateTeacher,
  schoolResetTeacherPassword,
  schoolDeleteTeacher,
  schoolRegisterStudent,
  schoolUpdateStudent,
  schoolResetStudentPassword,
  schoolDeleteStudent,
  schoolInviteStudent,
  revokeSchoolStudentInvite,
  resendSchoolStudentInvite,
  generateCheckQuestion,
  answerCheckQuestion,
  evaluateExplainBack,
  generateLessonStory,
  completeLessonStory,
  generateQuizRush,
  submitQuizRush,
  getDueReviewNudge
};
export default api;
